/**
 * Mehrere Dokumente in EINEM Editor-Tab (User 04.10.2026: „Dann müssen wir beim
 * Bearbeiten der PDFs auch Tabs anzeigen, sodass alle Tabs angezeigt werden in
 * einem Tab/PDF und man nicht immer springen muss“).
 *
 * Zwei Dinge stehen hier:
 *
 *  1. die LISTE der im Editor-Tab geöffneten Dokumente (reine Rechnung plus
 *     sessionStorage, damit sie ein Neuladen übersteht),
 *  2. das PROTOKOLL, mit dem KMVS ein weiteres Dokument in einen schon offenen
 *     Editor-Tab schickt, statt einen neuen Browser-Tab aufzumachen.
 *
 * Zum Protokoll: Browser-Tabs derselben Herkunft sprechen über einen
 * `BroadcastChannel`. Wer öffnen will, fragt („frage“); ein Editor-Tab meldet
 * sich („da“); der erste, der sich meldet, bekommt das Dokument („oeffnen“).
 * Antwortet niemand (kein Editor-Tab, oder Edge hat ihn schlafen gelegt), geht
 * wie bisher ein neuer Browser-Tab auf.
 *
 * Damit der häufigste Fall — es ist noch kein Editor-Tab offen — ohne Wartezeit
 * auskommt (und `window.open` synchron im Klick bleibt), hinterlässt jeder
 * Editor-Tab eine Marke im localStorage. Ohne Marke wird gar nicht erst gefragt.
 */

// ─── Liste der Dokument-Tabs ────────────────────────────────────────────────

/** Mehr Dokumente hält ein Editor-Tab nicht offen — jeder Tab hält sein PDF samt Editor im Speicher. */
export const MAX_DOK_TABS = 8;

export interface TabListe {
    /** Dokument-Ids in Tab-Reihenfolge. */
    tabs: number[];
    aktiv: number | null;
}

export const LEERE_LISTE: TabListe = { tabs: [], aktiv: null };

const istId = (v: unknown): v is number => typeof v === "number" && Number.isInteger(v) && v > 0;

/**
 * Dokument öffnen: ein schon offenes wird nur aktiv, ein neues kommt ans Ende.
 * `voll` = die Obergrenze ist erreicht, die Liste bleibt unverändert.
 */
export function tabOeffnen(l: TabListe, docId: number, max = MAX_DOK_TABS): { liste: TabListe; voll: boolean } {
    if (l.tabs.includes(docId)) return { liste: l.aktiv === docId ? l : { ...l, aktiv: docId }, voll: false };
    if (l.tabs.length >= max) return { liste: l, voll: true };
    return { liste: { tabs: [...l.tabs, docId], aktiv: docId }, voll: false };
}

/** Tab schließen. War er aktiv, rückt der rechte Nachbar nach — sonst der linke. */
export function tabSchliessen(l: TabListe, docId: number): TabListe {
    const i = l.tabs.indexOf(docId);
    if (i < 0) return l;
    const tabs = l.tabs.filter((t) => t !== docId);
    const aktiv = l.aktiv !== docId ? l.aktiv : (tabs[i] ?? tabs[i - 1] ?? null);
    return { tabs, aktiv };
}

/**
 * Liste beim Laden der Seite: die gemerkte Liste, dazu das Dokument aus der
 * URL — es ist das aktive. Passt es nicht mehr hinein, weicht der älteste Tab
 * (beim Laden ist noch nichts ungespeichert).
 */
export function startListe(gemerkt: TabListe | null, urlId: number | null, max = MAX_DOK_TABS): TabListe {
    const basis = gemerkt ?? LEERE_LISTE;
    if (urlId == null) return basis;
    if (basis.tabs.includes(urlId)) return { ...basis, aktiv: urlId };
    const platz = basis.tabs.slice(Math.max(0, basis.tabs.length - (max - 1)));
    return { tabs: [...platz, urlId], aktiv: urlId };
}

/** Gemerkte Liste lesen — alles Unbrauchbare (fremder Inhalt, Doppelte, zu viele) fällt heraus. */
export function listeLesen(roh: string | null, max = MAX_DOK_TABS): TabListe | null {
    if (!roh) return null;
    try {
        const d = JSON.parse(roh) as { tabs?: unknown; aktiv?: unknown };
        if (!Array.isArray(d.tabs)) return null;
        const tabs = [...new Set(d.tabs.filter(istId))].slice(0, max);
        if (tabs.length === 0) return null;
        return { tabs, aktiv: istId(d.aktiv) && tabs.includes(d.aktiv) ? d.aktiv : tabs[0] };
    } catch {
        return null;
    }
}

const LISTE_KEY = "kmvs.pdfEditor.tabs";

/** sessionStorage gehört dem Browser-Tab und übersteht ein Neuladen — genau die Lebensdauer der Tab-Zeile. */
export function gemerkteListe(): TabListe | null {
    try {
        return listeLesen(window.sessionStorage.getItem(LISTE_KEY));
    } catch {
        return null;
    }
}

export function listeMerken(l: TabListe): void {
    try {
        if (l.tabs.length === 0) window.sessionStorage.removeItem(LISTE_KEY);
        else window.sessionStorage.setItem(LISTE_KEY, JSON.stringify(l));
    } catch {
        /* Speicher gesperrt (privates Fenster, Richtlinie) — dann eben ohne Merken. */
    }
}

// ─── Protokoll zwischen Browser-Tabs ────────────────────────────────────────

const KANAL = "kmvs-pdf-editor";
const MARKE = "kmvs.pdfEditor.offen.";
const FENSTER = "kmvs-pdf-editor-";
/** So lange wartet der Öffnende auf einen Editor-Tab — spürbar kurz, für einen wachen Tab reichlich. */
export const WARTE_MS = 300;

type Nachricht =
    | { typ: "frage"; anfrage: string }
    | { typ: "da"; anfrage: string; tab: string; fenster: string }
    | { typ: "oeffnen"; tab: string; docId: number; bearbeiten: boolean };

const zufall = () => Math.random().toString(36).slice(2, 10);
const kanal = (): BroadcastChannel | null => {
    try {
        return typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(KANAL);
    } catch {
        return null;
    }
};

export const editorPfad = (docId: number, bearbeiten = false): string =>
    `/dokumente/${docId}/pdf${bearbeiten ? "?bearbeiten=1" : ""}`;

/** Gibt es (laut Marke) irgendwo einen offenen Editor-Tab? */
function editorTabVermerkt(): boolean {
    try {
        for (let i = 0; i < window.localStorage.length; i++) {
            if (window.localStorage.key(i)?.startsWith(MARKE)) return true;
        }
    } catch {
        /* kein localStorage → wie „keiner“. */
    }
    return false;
}

/** Marken, auf die niemand geantwortet hat (Absturz, geschlossener Browser), wegräumen. */
function markenVerwerfen(): void {
    try {
        const weg: string[] = [];
        for (let i = 0; i < window.localStorage.length; i++) {
            const k = window.localStorage.key(i);
            if (k?.startsWith(MARKE)) weg.push(k);
        }
        weg.forEach((k) => window.localStorage.removeItem(k));
    } catch {
        /* nichts zu tun */
    }
}

/**
 * Im Editor-Tab aufrufen: meldet ihn an und nimmt Dokumente entgegen, die KMVS
 * aus einem anderen Browser-Tab hierher schickt. Rückgabe = abmelden.
 */
export function editorTabAnmelden(onOeffnen: (docId: number, bearbeiten: boolean) => void): () => void {
    const tab = zufall();
    const k = kanal();
    if (!k) return () => undefined;
    const marke = MARKE + tab;
    const merken = () => {
        try {
            window.localStorage.setItem(marke, "1");
        } catch {
            /* ohne Marke fragt niemand — dann geht eben ein neuer Tab auf. */
        }
    };
    const vergessen = () => {
        try {
            window.localStorage.removeItem(marke);
        } catch {
            /* nichts zu tun */
        }
    };
    merken();
    k.onmessage = (e: MessageEvent<Nachricht>) => {
        const n = e.data;
        if (n?.typ === "frage") {
            // Ein anderer Tab hat aufgeräumt, während dieser schlief — Marke erneuern.
            merken();
            k.postMessage({ typ: "da", anfrage: n.anfrage, tab, fenster: window.name } satisfies Nachricht);
        } else if (n?.typ === "oeffnen" && n.tab === tab && istId(n.docId)) {
            onOeffnen(n.docId, n.bearbeiten === true);
        }
    };
    // `pagehide` statt `unload`: feuert auch, wenn Edge den Tab in den Seiten-Cache legt.
    window.addEventListener("pagehide", vergessen);
    return () => {
        window.removeEventListener("pagehide", vergessen);
        vergessen();
        k.close();
    };
}

/** Neuer Browser-Tab mit eigenem Fensternamen — so lässt er sich später nach vorn holen. */
function neuerEditorTab(docId: number, bearbeiten: boolean): void {
    // Jeder Editor-Tab bekommt einen EIGENEN Namen: ein fester Name würde einen
    // schlafenden Editor-Tab samt seiner ungespeicherten Arbeit überschreiben.
    window.open(editorPfad(docId, bearbeiten), FENSTER + zufall());
}

/**
 * Holt den Editor-Tab nach vorn. Das kann nur, wer ihn (oder dessen Vorfahren)
 * geöffnet hat: `window.open("", name)` liefert das vorhandene Fenster, ohne es
 * neu zu laden. Kennt dieser Tab das Fenster nicht, entsteht ein leeres — das
 * wird sofort wieder geschlossen, und der Aufrufer sagt dem Nutzer Bescheid.
 */
function nachVorn(fenster: string): boolean {
    if (!fenster.startsWith(FENSTER)) return false;
    try {
        const w = window.open("", fenster);
        if (!w) return false;
        if (w.location.href === "about:blank") {
            w.close();
            return false;
        }
        w.focus();
        return true;
    } catch {
        return false;
    }
}

export type OeffnenErgebnis =
    /** Neuer Browser-Tab (kein Editor-Tab offen oder keiner hat geantwortet). */
    | "neu"
    /** Als weiterer Tab im offenen Editor-Tab, der jetzt vorn ist. */
    | "vorhanden"
    /** Im offenen Editor-Tab geöffnet — der ließ sich aber nicht nach vorn holen. */
    | "vorhanden-hinten";

/**
 * Dokument im Editor-Tab öffnen. IM KLICK aufrufen: ohne offenen Editor-Tab
 * geht der neue Browser-Tab synchron auf (Popup-Blocker); mit einem wird
 * höchstens {@link WARTE_MS} gewartet — das liegt noch in der Frist, die
 * Chromium/Edge einem Klick für `window.open` einräumen.
 */
export async function oeffneImEditorTab(docId: number, opts?: { bearbeiten?: boolean }): Promise<OeffnenErgebnis> {
    const bearbeiten = opts?.bearbeiten === true;
    const k = editorTabVermerkt() ? kanal() : null;
    if (!k) {
        neuerEditorTab(docId, bearbeiten);
        return "neu";
    }
    const anfrage = zufall();
    const antwort = await new Promise<Extract<Nachricht, { typ: "da" }> | null>((fertig) => {
        const frist = window.setTimeout(() => fertig(null), WARTE_MS);
        k.onmessage = (e: MessageEvent<Nachricht>) => {
            if (e.data?.typ !== "da" || e.data.anfrage !== anfrage) return;
            window.clearTimeout(frist);
            fertig(e.data);
        };
        k.postMessage({ typ: "frage", anfrage } satisfies Nachricht);
    });
    if (!antwort) {
        k.close();
        markenVerwerfen();
        neuerEditorTab(docId, bearbeiten);
        return "neu";
    }
    k.postMessage({ typ: "oeffnen", tab: antwort.tab, docId, bearbeiten } satisfies Nachricht);
    k.close();
    return nachVorn(antwort.fenster) ? "vorhanden" : "vorhanden-hinten";
}
